import { z } from 'zod'
import { oc } from '@orpc/contract'
import { FAX_TASK_ACTIONS } from '../../care-tasks/fax-task-actions.schema'

const actionCodes = FAX_TASK_ACTIONS.map((a) => a.code) as [string, ...string[]]

export const createFaxTaskInputSchema = z
  .object({
    documentReferenceId: z.string().min(1),
    // Minted once per mounted create form; collapses double-submits without
    // blocking a deliberate second task on the same fax.
    submissionId: z.string().uuid(),
    // Fax-triage Task id (route: /fax/inbox/<id>), so the created board item
    // can deep-link back to the fax. Optional: the caller (web) only has it
    // when creating a task from the fax inbox route.
    faxTaskId: z.string().min(1).optional(),
    boardId: z.string().min(1),
    // Distinct codes: each action becomes one checklist step and one unit of
    // "+N more" in the derived name, so a repeat would render duplicate steps
    // and overstate the work. It is also what makes the max meaningful — there
    // are exactly this many actions to choose from.
    actions: z
      .array(z.enum(actionCodes))
      .min(1)
      .max(FAX_TASK_ACTIONS.length)
      .refine((codes) => new Set(codes).size === codes.length, {
        message: 'actions must not repeat',
      }),
    otherDescription: z.string().trim().min(1).max(500).optional(),
    priority: z.enum(['High', 'Medium', 'Low']),
    dueDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'dueDate must be YYYY-MM-DD')
      .optional(),
    patientReference: z
      .string()
      .regex(/^Patient\/[A-Za-z0-9._-]+$/)
      .optional(),
    ownerReference: z
      .string()
      .regex(/^Practitioner\/[A-Za-z0-9._-]+$/)
      .optional(),
  })
  .refine((v) => !v.actions.includes('other') || !!v.otherDescription, {
    message: 'otherDescription is required when the other action is selected',
    path: ['otherDescription'],
  })

export const createFaxTask = oc
  .route({ method: 'POST', path: '/v2/fax-tasks' })
  .input(createFaxTaskInputSchema)
  .output(z.object({ itemId: z.string() }))
